import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Camera, Image as ImageIcon, ArrowRight, FilmStrip, Trophy } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import { getMediaUrl } from '../lib/media';
import { activateFeature, useEngagement, engagementCounterText } from '../lib/features';
import { EventLivePills } from '../components/EventLivePills';
import { FeaturedTonightShelf } from '../components/FeaturedTonightShelf';
import { ProductCard } from '../components/ProductCard';
import { HeroCTA } from '../components/HeroCTA';
import { getGuestProducts, groupProductsByLayer } from '../shared/packageGuestProducts';
import { saveGuestSession, loadGuestSession } from '../shared/guestSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestDashboard = () => {
  const { slug, guestId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [guest, setGuest] = useState(null);
  const [myMedia, setMyMedia] = useState([]);
  const [totalMoments, setTotalMoments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const activatedRef = useRef(new Set());
  const { counts: engagementCounts } = useEngagement(event?.event_id, axios, API);

  useEffect(() => {
    loadAll();
    const iv = setInterval(loadMyMedia, 5000);
    return () => clearInterval(iv);
  }, [slug, guestId]);

  const loadAll = async () => {
    try {
      const [evRes, gRes] = await Promise.all([
        axios.get(`${API}/events/slug/${slug}`),
        axios.get(`${API}/rsvp/${guestId}`),
      ]);
      setEvent(evRes.data);
      setGuest(gRes.data);
      // iter186 — persist the guest identity for this tab session so
      // browser-back from any guest product never bounces the user
      // back to the name-lookup screen.
      saveGuestSession({
        slug,
        guest_id: guestId,
        guest_name: gRes.data?.guest_name || '',
      });
      loadMyMedia(evRes.data.event_id);
    } catch {
      // Network/lookup failure — try to resume from sessionStorage so
      // a flaky reload doesn't dump the guest at the lookup screen.
      const cached = loadGuestSession(slug);
      if (!cached) {
        // No cache either — let the lookup flow handle it.
      }
    }
    finally { setLoading(false); }
  };

  const loadMyMedia = async (eid) => {
    const eventId = eid || event?.event_id;
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/guests/${guestId}/media`);
      setMyMedia(res.data.media || []);
    } catch {}
    try {
      const all = await axios.get(`${API}/events/${eventId}/photos`);
      setTotalMoments(all.data?.length || 0);
    } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/events/${event.event_id}/photos?source=guest_${guestId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Memory saved!');
      loadMyMedia();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleRsvp = async (status) => {
    try {
      await axios.post(`${API}/rsvp/${guestId}`, { status });
      setGuest(prev => ({ ...prev, rsvp_status: status }));
      toast.success(status === 'attending' ? 'See you there!' : 'Response recorded');
    } catch {}
  };

  if (loading) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;
  if (!event || !guest) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center text-white text-center px-6"><p className="text-xl font-bold">Page not found</p></div>;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(circle at top, #1a1a2e, #15161B)', fontFamily: 'Inter, sans-serif' }} data-testid="guest-dashboard">
      <div className="max-w-lg mx-auto px-6 py-8">
        <div className="mb-4">
          <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="guest-dashboard-back" />
        </div>
        {/* Header */}
        <div className="text-center mb-6">
          <EventDropLogo size={36} className="mx-auto mb-3 opacity-70" />
          <h1 className="text-2xl font-black text-white tracking-tight mb-1" data-testid="guest-event-name">{event.event_name}</h1>
          {event.event_date && <p className="text-white/40 text-sm">{event.event_date}</p>}
        </div>

        {/* Welcome */}
        <div className="text-center mb-6">
          <p className="text-white/60 text-base">Welcome back, <span className="text-white font-bold">{guest.guest_name}</span></p>
        </div>

        {/* RSVP Status */}
        <div className="rounded-2xl p-4 mb-5 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="guest-rsvp-status">
          {guest.rsvp_status === 'attending' ? (
            <div className="flex items-center justify-center gap-2 text-[#B89762] font-bold text-sm"><Check size={18} weight="bold" /> You're attending</div>
          ) : guest.rsvp_status === 'declined' ? (
            <div className="flex items-center justify-center gap-2 text-red-400 font-bold text-sm"><X size={18} weight="bold" /> You declined</div>
          ) : (
            <div>
              <p className="text-white/50 text-sm mb-3">Will you be joining?</p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => handleRsvp('attending')} className="px-5 py-2.5 rounded-xl font-bold text-white text-sm flex items-center gap-1.5" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }} data-testid="guest-rsvp-yes"><Check size={14} weight="bold" /> Yes</button>
                <button onClick={() => handleRsvp('declined')} className="px-5 py-2.5 rounded-xl font-bold text-white/50 text-sm border border-white/10" data-testid="guest-rsvp-no"><X size={14} weight="bold" className="inline mr-1" /> No</button>
              </div>
            </div>
          )}
        </div>

        {/* PRIMARY UPLOAD — gold-glow glass HeroCTA matching the
            ProductCard aesthetic below. Replaced the legacy violet→
            teal gradient pill 2026-05-12 so the entire dashboard
            reads as a cohesive cinematic surface. */}
        <div className="mb-5">
          <HeroCTA
            testId="upload-moments-btn"
            title="Share your moments"
            subtitle="Add a moment to tonight"
            icon={Camera}
            accent="gold"
            busy={uploading}
            busyLabel="Uploading…"
            onClick={() => fileRef.current?.click()}
          />
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleUpload} />

        {/* Stats Strip */}
        <div className="flex justify-center gap-6 mb-6">
          <div className="text-center">
            <p className="text-xl font-bold text-white">{myMedia.length}</p>
            <p className="text-white/30 text-xs">Your uploads</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-[#B89762]">{totalMoments}</p>
            <p className="text-white/30 text-xs">Total moments</p>
          </div>
          {myMedia.length >= 5 && (
            <div className="text-center">
              <Trophy size={20} weight="fill" className="text-amber-400 mx-auto" />
              <p className="text-white/30 text-xs">Top contributor</p>
            </div>
          )}
        </div>

        {/* "🏆 Featured tonight" — Live wall shelf. Auto-hides until
            the event has at least one featured spin. Creates the
            "I want to get featured too" loop without ever exposing
            ranks/points/leaderboards. */}
        <FeaturedTonightShelf eventId={event.event_id} />

        {/* Your Moments */}
        {myMedia.length > 0 && (
          <div className="mb-6" data-testid="guest-my-moments">
            <h3 className="text-white font-bold text-sm mb-3">Your Moments</h3>
            <div className="grid grid-cols-3 gap-2">
              {myMedia.map((m, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-[#15161B]" style={{ animation: `fadeIn .3s ease-out ${i * 0.05}s both` }}>
                  {m.content_type?.startsWith('video/') ? (
                    <video src={getMediaUrl(m.storage_path, `${API}/photos/${m.storage_path}`)} className="w-full h-full object-cover" />
                  ) : (
                    <img src={getMediaUrl(m.storage_path, `${API}/photos/${m.storage_path}`)} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CANONICAL PRODUCT GRID (iter186) — single source of truth.
            Renders the exact ordered list defined in
            /app/frontend/src/shared/packageGuestProducts.js for the
            event's package. No silent filtering, no inline ordering
            heuristics. If a card appears here, its route resolves to
            the experience that card promises.

            Trust failure cured: previously this grid filtered
            event.features through FEATURE_CARDS + GUEST_HIDDEN_FEATURES
            + an inline EMOTIONAL_ORDER array — Archive guests saw an
            incomplete set with cards routing to wrong destinations
            (Booth OS → uploader · Flip Guestbook → audio guestbook).
            Now: package → ordered product list → typed route. */}
        {(() => {
          const products = getGuestProducts(event);
          if (!products.length) return null;
          // iter208 — Re-express the flat product list as the 4
          // emotional layers. Guest-facing surface → aftermath
          // register, so the archive layer reads "What remains ✦"
          // (matching the homepage / Schools page vocabulary).
          const layers = groupProductsByLayer(products, { register: 'aftermath' });
          return (
            <div data-testid="guest-dashboard-feature-launchers">
              <EventLivePills counts={engagementCounts} testId="guest-dashboard-live-pills" />
              <h3
                data-testid="guest-dashboard-do-header"
                className="text-white/95 mb-3"
                style={{
                  fontSize: 22,
                  fontWeight: 400,
                  letterSpacing: '-0.02em',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}
              >
                Tonight's experiences
              </h3>
              {layers.map((layer, layerIdx) => (
                <section
                  key={layer.id}
                  data-testid={`guest-dashboard-layer-${layer.id}`}
                  style={{ marginBottom: layerIdx === layers.length - 1 ? 8 : 32 }}
                >
                  <h4
                    data-testid={`guest-dashboard-layer-title-${layer.id}`}
                    style={{
                      margin: 0, marginBottom: 4,
                      fontSize: 11,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'rgba(184,151,98,0.85)',
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontStyle: 'italic',
                      fontWeight: 400,
                    }}
                  >
                    {layer.title}
                  </h4>
                  <p
                    style={{
                      margin: 0, marginBottom: 14,
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.42)',
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontStyle: 'italic',
                    }}
                  >
                    {layer.subtitle}
                  </p>
                  <div
                    className="grid"
                    style={{
                      gap: 14,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
                      alignItems: 'stretch',
                    }}
                  >
                    {layer.items.map((product) => {
                      const counterText = engagementCounterText(product.id, engagementCounts);
                      const handleClick = () => {
                        activateFeature(axios, API, event.event_id, product.id, activatedRef.current);
                        if (product.action === 'open-uploader') {
                          fileRef.current?.click();
                          return;
                        }
                        if (typeof product.route === 'function') {
                          navigate(product.route(slug, event));
                        }
                      };
                      return (
                        <ProductCard
                          key={product.id}
                          testId={`guest-dashboard-launch-${product.id}`}
                          title={product.title}
                          subtitle={product.subtitle}
                          icon={product.icon}
                          accent={product.accent || 'gold'}
                          meta={counterText}
                          onClick={handleClick}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          );
        })()}

        {/* Quick Links */}
        <div className="space-y-2 mb-6">
          <a href={`/event/${slug}/guest/gallery`} className="flex items-center justify-between px-4 py-3.5 rounded-xl text-white/60 text-sm font-medium hover:bg-[#15161B] transition-colors" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="view-gallery-link">
            <span className="flex items-center gap-2"><ImageIcon size={16} weight="bold" /> View Full Gallery</span>
            <ArrowRight size={14} weight="bold" className="text-white/20" />
          </a>
        </div>

        {/* "Tonight's experiences" is the rich, cinematic guest-facing
            grid above. The Mode 1 companion mirror that previously
            lived here was redundant — guest sees the same products
            twice. Mode 1 cards are now reserved for the host-side
            "Included in this edition ✦" mirror on EventPage / Dashboard. */}

        {/* Film tease */}
        <div className="rounded-2xl p-5 text-center mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="film-tease">
          <FilmStrip size={24} weight="bold" className="text-[#B89762] mx-auto mb-2" />
          <p className="text-white/40 text-sm">Your moments will be part of the event film</p>
        </div>

        <p className="text-white/10 text-xs text-center">Powered by EventDrop</p>
      </div>
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
};

export default GuestDashboard;
